import { useState, useEffect, useCallback, useContext } from 'react'
import PropTypes from 'prop-types'
import { message, Modal, Button, Input, Radio } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import Moment from 'react-moment'
import { useGate } from 'statsig-react'

import { standardDate } from '@/utils/dates.js'
import { getStoresToChange, changeJobStore } from './change-store.services'
import { HistoricalContext } from '../../../refactory/modules/jobs/infrastructure/job-detail/historical.context'

// Styles
import './styles.less'

const { Search, TextArea } = Input

const ChangeStoreModal = ({
  job: { id: jobId, slotFrom, slotTo, storeId: currentStoreId, origin, operationModel },
  currentUser: { user },
  title,
  timeZone,
  visible,
  setSelectedJob,
  jobsSearch,
  handleVisible,
  translation
}) => {
  const [step, setStep] = useState(0)
  const [selectedStore, setSelectedStore] = useState(null)
  const [stores, setStores] = useState(null)
  const [reason, setReason] = useState(null)
  const [anotherReason, setAnotherReason] = useState('')
  const [comment, setComment] = useState('')
  const [previousStore, setPreviousStore] = useState(null)
  const { getJobHistorical } = useContext(HistoricalContext)
  const isGetDetailJobByNebulaActive = useGate('is_get_detail_job_by_nebula_active').value

  const onChangeReason = ({ target: { value } }) => {
    setReason(value)
  }

  const onChangeAnotherReason = ({ target: { value } }) => {
    setAnotherReason(value)
  }

  const onChangeComment = ({ target: { value } }) => {
    setComment(value)
  }

  const getStores = useCallback(
    async (storeName = null) => {
      try {
        const storesToChange = await getStoresToChange(jobId, storeName)
        setStores(storesToChange)
      } catch (error) {
        console.error('something went wrong getting the stores')
      }
    },
    [jobId]
  )

  const reasons = [
    translation('TheDestinationLocationIsFarFromTheInitialStore'),
    translation('TheProductsOfTheOrderAreOutOfStockInTheInitialStore'),
    translation('TheStarterStoreHasLogisticalProblems'),
    translation('TheCustomerWantsADifferentCollectionStore')
  ]

  const steps = [
    <div className='csm-step-one'>
      <h3>1. {translation('selectTheStoreToMoveTheJob')}</h3>
      <div className='csm-step-one-container'>
        <Search
          className='csm-step-one-search'
          placeholder={translation('searchStores')}
          allowClear
          enterButton
          size='large'
          onSearch={(storeName) => getStores(storeName)}
        />
        <div className='csm-step-one-stores'>
          {stores && stores.length > 0 ? (
            stores.map((store, idx) => {
              const { id, name, address } = store
              return (
                <div key={idx} className='csm-step-one-store' onClick={() => setSelectedStore(store)}>
                  <div className='csm-step-one-store-data'>
                    <span>{name}</span>
                    <span>{address}</span>
                  </div>
                  {selectedStore && selectedStore.id === id && <CheckOutlined />}
                </div>
              )
            })
          ) : (
            <div className='csm-step-one-no-stores'>
              {stores && stores.length === 0 && <span>{translation('thereAreNotStoresAvailable')}</span>}
            </div>
          )}
        </div>
        <p>{translation('storesThaSupportTheSameOperationalModel')}</p>
      </div>
    </div>,
    <div className='csm-step-two'>
      <h3>2. {translation('whyAreYouChangingTheStoreFromThisJob')}</h3>
      <div className='csm-step-two-container'>
        <Radio.Group onChange={onChangeReason} value={reason}>
          {reasons.map((_reason, idx) => {
            return (
              <Radio key={idx} value={_reason}>
                {_reason}
              </Radio>
            )
          })}
          <Radio key={'-1'} value='another'>
            <Input
              allowClear
              placeholder={translation('another')}
              size='large'
              value={anotherReason}
              onChange={onChangeAnotherReason}
            />
          </Radio>
        </Radio.Group>
      </div>
    </div>,
    <div className='csm-step-three'>
      <h3>3. {translation('confirmRelocationOfThisJobToANewStore')}</h3>
      {selectedStore && (
        <div className='csm-step-three-container'>
          <div className='csm-step-three-store-info'>
            <div className='store-name'>{selectedStore.name}</div>
            <div className='store-address'>{selectedStore.address}</div>
            <div className='store-operational-model'>
              <strong>{translation('operationModel')}</strong>
              <span>{translation(`job-models.${operationModel}`)}</span>
            </div>
            <div className='store-delivery-time'>
              <strong>{translation('deliveryDate')}</strong>
              <span>
                <Moment format={`D [${translation('of')}] MMMM hh:mm a`} tz={timeZone}>
                  {standardDate(slotFrom)}
                </Moment>
                {' - '}
                <Moment format='hh:mm a' tz={timeZone}>
                  {standardDate(slotTo)}
                </Moment>
              </span>
            </div>
          </div>
          <div className='csm-step-three-comment'>
            <span>{translation('optionalComment')}</span>
            <TextArea
              showCount
              maxLength={400}
              value={comment}
              onChange={onChangeComment}
              rows={7}
              placeholder={translation('addAComment')}
            />
          </div>
        </div>
      )}
    </div>
  ]

  const isNextButton = step < steps.length - 1
  const isBackButton = step > 0

  const previous = () => {
    if (isBackButton) {
      setStep((prev) => prev - 1)
    }
  }

  const next = () => {
    if (isNextButton) {
      setStep((prev) => prev + 1)
    }
  }

  const showSuccessMessage = (storeName) => {
    message.success({
      content: (
        <div className='csm-success-message-container'>
          <span>{translation('orderTransferredTo', { storeName })}</span>
          <div className='csm-success-message-split' />
          <Button type='link' onClick={() => changeStore(previousStore, translation('revertChangeStore'), '', true)}>
            {translation('undoThisAction')}
          </Button>
        </div>
      ),
      className: 'csm-success-message',
      duration: 20
    })
  }

  const changeStore = async (store, _reason, _comment, isRevert = false) => {
    try {
      const jobInfo = await changeJobStore(jobId, store.id, {
        reason: _reason,
        comment: _comment,
        newStoreName: store.name
      })
      handleVisible(false)
      if (isRevert) {
        message.success(translation('revertChangeStoreSuccessMessage'))
      } else {
        showSuccessMessage(store.name)
      }
      if (!isGetDetailJobByNebulaActive) {
        getJobHistorical(jobId)
      }
      if (jobsSearch) {
        setSelectedJob(jobInfo)
      }
    } catch (error) {
      console.error('something went wrong changing job store')
    }
  }

  const cleanState = () => {
    setStep(0)
    setSelectedStore(null)
    setReason(null)
    setAnotherReason('')
    setComment('')
  }

  const isNextButtonDisabled = () => {
    if (step === 0 && !selectedStore) {
      return true
    }
    if (step === 1 && (!reason || (reason === 'another' && anotherReason === ''))) {
      return true
    }
    return false
  }

  useEffect(() => {
    if (!visible) {
      cleanState()
    } else if (jobId) {
      getStores()
      setPreviousStore({
        id: currentStoreId,
        name: origin.name
      })
    }
  }, [currentStoreId, getStores, jobId, origin.name, visible])

  return (
    <Modal
      title={title}
      wrapClassName='csm'
      centered
      width={600}
      closable={true}
      visible={visible}
      footer={null}
      onCancel={() => handleVisible(false)}
    >
      <div className='csm-container'>{steps[step]}</div>
      <div className='csm-footer'>
        <div className='csm-responsible'>
          <p>{translation('responsible')}:</p>
          <strong>{`${user.firstName} ${user.lastName}`}</strong>
        </div>
        <div className='csm-buttons'>
          <Button type='default' onClick={() => (isBackButton ? previous() : handleVisible(false))}>
            {`${isBackButton ? translation('back') : translation('cancel')}`}
          </Button>
          <Button
            type='primary'
            onClick={() => (isNextButton ? next() : changeStore(selectedStore, reason, comment))}
            disabled={isNextButtonDisabled()}
          >
            {`${isNextButton ? translation('next') : translation('continue')}`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

ChangeStoreModal.propTypes = {
  title: PropTypes.string,
  visible: PropTypes.bool,
  handleVisible: PropTypes.func,
  currentUser: PropTypes.shape({
    user: PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string
    })
  }),
  job: PropTypes.shape({
    uuid: PropTypes.string,
    slotFrom: PropTypes.string,
    slotTo: PropTypes.string,
    city: PropTypes.shape({
      timeZone: PropTypes.string
    })
  }),
  translation: PropTypes.func
}

ChangeStoreModal.defaultProps = {
  title: '',
  visible: false,
  handleVisible: () => {},
  currentUser: {
    user: {
      firstName: '',
      lastName: ''
    }
  },
  job: {
    uuid: '',
    slotFrom: '',
    slotTo: '',
    city: {
      timeZone: ''
    }
  },
  translation: (text) => text
}

export default ChangeStoreModal
